import type {Filter} from '../types.js';
import type {CategoryOrderBy} from '../widget-sources/types.js';

export type ControlBinding = {
  datasetId: string;
  column: string;
};

type TextValue = string | null;

export type TextSelection = 'all' | TextValue[];

type ControlInFilter = {in: {values: TextValue[]}};

export type ControlTimeFilter = {time: {values: number[][]}};

type ControlNotInFilter = {not_in: {values: TextValue[]}};

export type ControlFilter =
  ControlInFilter | ControlNotInFilter | ControlTimeFilter;

export type FilterListValues = NonNullable<Filter['in']>['values'];

type IntersectResult = {empty: true} | {empty: false; filter: Filter};

type IsoDate = string;

type DateUnit = 'day' | 'month' | 'year';

type DateAnchor = 'today' | 'data';

type DateDefault =
  | {kind: 'all'}
  | {kind: 'fixed'; start: IsoDate; end: IsoDate}
  | {
      kind: 'relative';
      direction: 'last';
      count: number;
      unit: DateUnit;
      anchor: DateAnchor;
    }
  | {
      kind: 'relative';
      direction: 'next';
      count: number;
      unit: DateUnit;
      anchor: 'today';
    }
  | {kind: 'from'; start: IsoDate; anchor: DateAnchor};

type DateWindow = {start: IsoDate; end: IsoDate};

type DateSelection = DateWindow | 'all';

export type ControlParameter = {
  id?: string;
  type: string;
  binding?: ControlBinding;
  selection?: TextSelection | DateSelection;
  default?: DateDefault;
  selectionMode?: string;
  defaultValue?: string;
  orderBy?: CategoryOrderBy;
};

const SOURCE_CONTROL_TYPES = ['SourceCategory', 'SourceDateRange'];

export function isSourceBoundTo(
  parameter: ControlParameter | undefined,
  dataset: {id: string}
): parameter is ControlParameter & {binding: ControlBinding} {
  const binding = parameter?.binding;
  return (
    SOURCE_CONTROL_TYPES.includes(parameter?.type as string) &&
    binding?.datasetId === dataset.id &&
    typeof binding.column === 'string'
  );
}

type Ymd = {y: number; m: number; d: number};

function daysInMonth(y: number, m: number): number {
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

function parseIso(value: unknown): Ymd | undefined {
  if (
    typeof value !== 'string' ||
    !/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(value)
  ) {
    return undefined;
  }
  const y = Number(value.slice(0, 4));
  const m = Number(value.slice(5, 7));
  const d = Number(value.slice(8, 10));
  return y >= 1000 && m >= 1 && m <= 12 && d >= 1 && d <= daysInMonth(y, m)
    ? {y, m, d}
    : undefined;
}

export function isIsoDate(value: unknown): value is IsoDate {
  return parseIso(value) !== undefined;
}

export function toWindow(start: unknown, end: unknown): DateWindow | undefined {
  return isIsoDate(start) && isIsoDate(end) && start <= end
    ? {start, end}
    : undefined;
}

function resolveDateDefault(rule: DateDefault): DateSelection | undefined {
  switch (rule?.kind) {
    case 'all':
      return 'all';
    case 'fixed':
      return toWindow(rule.start, rule.end);
    default:
      return undefined;
  }
}

export function isTextValue(value: unknown): value is TextValue {
  return value === null || typeof value === 'string';
}

export function isNonEmptyTextList(list: unknown): list is TextValue[] {
  return Array.isArray(list) && list.length > 0 && list.every(isTextValue);
}

export function isExceptSelection(
  selection: unknown
): selection is {except: TextValue[]} {
  return (
    typeof selection === 'object' &&
    selection !== null &&
    !Array.isArray(selection) &&
    Array.isArray((selection as {except?: unknown}).except)
  );
}

function encodeTextSelection(
  selection: unknown
): ControlInFilter | ControlNotInFilter | undefined {
  if (isExceptSelection(selection)) {
    const {except} = selection;
    return isNonEmptyTextList(except)
      ? {not_in: {values: [...new Set(except)]}}
      : undefined;
  }
  if (!Array.isArray(selection) || !isNonEmptyTextList(selection)) {
    return undefined;
  }
  return {in: {values: [...new Set<TextValue>(selection)]}};
}

function dateWindowFilter(window: DateWindow): ControlTimeFilter | undefined {
  const valid = toWindow(window?.start, window?.end);
  if (!valid) {
    return undefined;
  }
  const first = parseIso(valid.start)!;
  const last = parseIso(valid.end)!;
  return {
    time: {
      values: [
        [
          Date.UTC(first.y, first.m - 1, first.d),
          Date.UTC(last.y, last.m - 1, last.d + 1) - 1,
        ],
      ],
    },
  };
}

export function singleDefaultSelection(
  control: Pick<ControlParameter, 'selectionMode' | 'defaultValue'>
): string[] | undefined {
  return control.selectionMode === 'single' &&
    typeof control.defaultValue === 'string'
    ? [control.defaultValue]
    : undefined;
}

export function controlFilter(
  control: ControlParameter,
  selection: unknown
): ControlFilter | undefined {
  if (control?.type === 'SourceCategory') {
    const stored =
      (Array.isArray(selection) && selection.length === 0) ||
      (control.selectionMode === 'single' && isExceptSelection(selection))
        ? undefined
        : selection;
    return encodeTextSelection(
      stored === undefined ? singleDefaultSelection(control) : stored
    );
  }
  if (control?.type !== 'SourceDateRange') {
    return undefined;
  }
  const window =
    selection === undefined && control.default
      ? resolveDateDefault(control.default)
      : selection;
  return window === 'all' ? undefined : dateWindowFilter(window as DateWindow);
}

function stringifyUnlessNull(
  value: string | number | boolean | null
): TextValue {
  return value === null ? null : String(value);
}

export function intersectFilters(
  current: Filter | undefined,
  control: ControlFilter
): IntersectResult {
  const filter: Filter = {...current};
  if ('in' in control) {
    const allowed: (string | number | boolean | null)[] | undefined =
      current?.in?.values;
    if (allowed === undefined) {
      filter.in = {values: [...control.in.values] as FilterListValues};
    } else {
      const allowedValues = new Set(allowed.map(stringifyUnlessNull));
      const values = control.in.values.filter((value) =>
        allowedValues.has(value)
      );
      if (values.length === 0) {
        return {empty: true};
      }
      filter.in = {values: values as FilterListValues};
    }
    return {empty: false, filter};
  }
  if ('not_in' in control) {
    const excluded: (string | number | boolean | null)[] =
      current?.not_in?.values ?? [];
    filter.not_in = {
      values: [
        ...new Set([
          ...excluded.map(stringifyUnlessNull),
          ...control.not_in.values,
        ]),
      ] as FilterListValues,
    };
    return {empty: false, filter};
  }
  const [controlStart, controlEnd] = control.time.values[0];
  const currentRanges = current?.time?.values;
  if (currentRanges === undefined) {
    filter.time = {values: control.time.values.map((range) => [...range])};
    return {empty: false, filter};
  }
  const values: number[][] = [];
  for (const range of currentRanges) {
    if (!Array.isArray(range)) {
      continue;
    }
    const rangeStart = range[0] ?? -Infinity;
    const rangeEnd = range[1] ?? Infinity;
    if (typeof rangeStart !== 'number' || typeof rangeEnd !== 'number') {
      continue;
    }
    const start = Math.max(rangeStart, controlStart);
    const end = Math.min(rangeEnd, controlEnd);
    if (start <= end) {
      values.push([start, end]);
    }
  }
  if (values.length === 0) {
    return {empty: true};
  }
  filter.time = {values};
  return {empty: false, filter};
}
